import { FC, memo, ReactNode, useCallback, useEffect, useLayoutEffect } from "react"

import { Outlet, useLocation, useNavigate } from "react-router"

import { AppState, Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import { SWRConfig } from "swr"

import { captureException } from "@/lib/Tracing"

import ErrorWithData from "@/utils/ErrorWithData"
import { s } from "@/utils/i18n"

import useAppSync from "@/hooks/useAppSync"
import useTheme from "@/hooks/useTheme"

import { Button } from "@/components/Button"
import ErrorUnwrapper from "@/components/ErrorUnwrapper"

import ContextLargeIcon from "@/icons/ContextLarge"

import { HistoryContextProvider } from "@/context/HistoryContext"
import { ModalContextProvider } from "@/context/ModalContext"

import NotFoundPage from "./NotFound"

export const GlobalLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center gap-2">
    <ContextLargeIcon config="LAVALAMP" />
    {/* <ProxyCircleIcon width="24" height="24" className="animate-3000 animate-spin will-change-transform" />
    <H5 as="div">{s`PROXY`}</H5> */}
  </div>
)

const AuthWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoading, error, getIdTokenClaims, logout, isAuthenticated } = useAuth0()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (pathname === "/register" || pathname === "/return" || isLoading || !isAuthenticated) {
      return
    }
    getIdTokenClaims().then((claims) => {
      if (
        !claims?.["https://system-operating.ai/subscriptionId"] ||
        claims?.["https://system-operating.ai/subscriptionStatus"] !== "active"
      ) {
        navigate("/register", { replace: true })
      }
    })
  }, [error, getIdTokenClaims, isAuthenticated, isLoading, navigate, pathname])
  if (isLoading) {
    return <GlobalLoader />
  }
  if (error) {
    captureException(error)
    return (
      <NotFoundPage
        error={
          <>
            {error.message || s`Authentication Error`}
            <ErrorUnwrapper error={new ErrorWithData(error?.message, error)} />
          </>
        }
      >
        <Button
          as="button"
          size="small"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >{s`Sign out`}</Button>
      </NotFoundPage>
    )
    // throw new ErrorWithData(error?.message, error)
  }

  return children
}

const SWRContext: FC<{ children: ReactNode }> = ({ children }) => {
  const { fetcher } = useAppSync()
  return (
    <SWRConfig
      value={{
        fetcher,
      }}
    >
      {children}
    </SWRConfig>
  )
}

export const RootLayout = ({ children }: { children?: React.ReactNode }) => {
  const navigate = useNavigate()

  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      navigate(appState?.returnTo || window.location.pathname)
    },
    [navigate]
  )

  const [theme] = useTheme()

  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", theme)
  }, [theme])

  return (
    <HistoryContextProvider>
      <meta name="theme-color" content={theme === "dark" ? "#1a1a1a" : "#fdfdfd"} />
      <Auth0Provider
        domain={import.meta.env.VITE_PUBLIC_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_PUBLIC_AUTH0_CLIENT_ID}
        legacySameSiteCookie={false}
        authorizationParams={{
          redirect_uri: new URL("calendar", window.location.origin).toString(),
          audience: import.meta.env.VITE_PUBLIC_AUTH0_AUDIENCE,
        }}
        onRedirectCallback={onRedirectCallback}
      >
        <AuthWrapper>
          <SWRContext>
            <ModalContextProvider>{children || <Outlet />}</ModalContextProvider>
          </SWRContext>
        </AuthWrapper>
      </Auth0Provider>
    </HistoryContextProvider>
  )
}
export default memo(RootLayout)
